/**
 * Documentation Page
 *
 * How to use CrowdTalkie, safety tips, and FAQ.
 */

import { Link } from 'react-router-dom'
import { useState } from 'react'
import { clsx } from 'clsx'
import { InfoCard } from '../components/common'
import { ENFORCEMENT_REPORTS } from '../lib/features'

/** FAQ entries about the Vehicles module, hidden where it is off. */
const ENFORCEMENT_FAQ = /vehicle|license plate|geo-tagging for photos|zone handoff/i

type DocSection = 'getting-started' | 'roles' | 'offline' | 'safety' | 'features' | 'command' | 'faq'

export function DocsPage() {
  const [activeSection, setActiveSection] = useState<DocSection>('getting-started')

  return (
    <div className="flex-1 flex flex-col relative overflow-hidden font-body">
      {/* Header */}
      <header className="flex items-center gap-4 p-4 border-b border-white/10 glass-panel z-10">
        <Link
          to="/"
          className="text-white/60 hover:text-primary transition-colors text-sm font-mono tracking-wider"
        >
          [ BACK ]
        </Link>
        <h1 className="text-lg font-display font-bold uppercase tracking-widest text-primary neon-glow">Field Manual</h1>
      </header>

      {/* Tab navigation */}
      <nav className="flex border-b border-white/10 overflow-x-auto bg-black/20 pb-1">
        {[
          { id: 'getting-started', label: '🚀 Start', fullLabel: 'INITIATION' },
          { id: 'roles', label: '👥 Roles', fullLabel: 'ROLES' },
          { id: 'offline', label: '📴 Offline', fullLabel: 'OFFLINE OPS' },
          { id: 'safety', label: '🛡️ Safety', fullLabel: 'SECURITY' },
          { id: 'features', label: '✨ Features', fullLabel: 'CAPABILITIES' },
          { id: 'command', label: '📡 Command', fullLabel: 'COMMAND' },
          { id: 'faq', label: '❓ FAQ', fullLabel: 'FAQ' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSection(tab.id as DocSection)}
            className={clsx(
              'px-6 py-4 text-xs font-bold whitespace-nowrap transition-colors uppercase tracking-widest font-mono',
              activeSection === tab.id
                ? 'text-primary border-b-2 border-primary bg-primary/5'
                : 'text-white/40 hover:text-white hover:bg-white/5'
            )}
          >
            <span className="md:hidden">{tab.label}</span>
            <span className="hidden md:inline">{tab.fullLabel}</span>
          </button>
        ))}
      </nav>

      {/* Content */}
      <main className="flex-1 p-4 overflow-y-auto space-y-6 scrollbar-hide">
        {activeSection === 'getting-started' && <GettingStartedSection />}
        {activeSection === 'roles' && <RolesSection />}
        {activeSection === 'offline' && <OfflineSection />}
        {activeSection === 'safety' && <SafetySection />}
        {activeSection === 'features' && <FeaturesSection />}
        {activeSection === 'command' && <CommandSection />}
        {activeSection === 'faq' && <FAQSection />}

        <div className="pt-8 pb-4 text-center">
          <p className="text-[10px] font-mono text-white/20 uppercase tracking-[0.3em]">
            // End of Document //
          </p>
        </div>
      </main>
    </div>
  )
}

function GettingStartedSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <InfoCard title="What is CrowdTalkie?">
        <p>
          CrowdTalkie is a <strong>privacy-first</strong> walkie-talkie app for group coordination.
          It connects people directly without needing a central server.
        </p>
      </InfoCard>

      <InfoCard title="Creating a Room">
        <ol className="list-decimal list-inside space-y-3 font-mono text-sm text-white/70">
          <li className="pl-2">Tap <strong className="text-primary">CREATE ROOM</strong> on home screen</li>
          <li className="pl-2">Name your room (e.g., "Downtown Meetup")</li>
          <li className="pl-2">Enter your display name</li>
          <li className="pl-2">Allow location access</li>
          <li className="pl-2">Set how far the room reaches (50m - 500m)</li>
          <li className="pl-2">Share the link or QR code with others</li>
        </ol>
      </InfoCard>

      <InfoCard title="Joining a Room">
        <ol className="list-decimal list-inside space-y-3 font-mono text-sm text-white/70">
          <li className="pl-2">Get the room link from the organizer</li>
          <li className="pl-2">Tap <strong className="text-primary">JOIN ROOM</strong></li>
          <li className="pl-2">Scan QR code or paste the link</li>
          <li className="pl-2">Enter your display name</li>
          <li className="pl-2">Make sure you're in the room's area</li>
        </ol>
      </InfoCard>

      <InfoCard title="Voice Messages">
        <ol className="list-decimal list-inside space-y-3 font-mono text-sm text-white/70">
          <li className="pl-2">Allow microphone access</li>
          <li className="pl-2">Hold the talk button to record</li>
          <li className="pl-2">Speak clearly (max 30 seconds)</li>
          <li className="pl-2">Release to send</li>
          <li className="pl-2">Your message is automatically transcribed</li>
        </ol>
      </InfoCard>

      <InfoCard title="Joining from Map">
        <p className="mb-3 text-sm">When you tap a room on the discovery map:</p>
        <ol className="list-decimal list-inside space-y-3 font-mono text-sm text-white/70">
          <li className="pl-2"><strong className="text-primary">Enter Secret</strong> - If you have the room secret and/or PIN from the organizer</li>
          <li className="pl-2"><strong className="text-primary">Request Access</strong> - Ask the organizer for permission to join</li>
        </ol>
        <p className="mt-4 text-xs text-white/50">
          Room secrets are shared by organizers via QR code or secure message. Never share room secrets publicly.
        </p>
      </InfoCard>

      <InfoCard title="Quick Reports">
        <p className="mb-3 text-sm">Share location updates with one tap:</p>
        <div className="grid grid-cols-2 gap-3 text-xs font-mono font-bold uppercase tracking-wider">
          {ENFORCEMENT_REPORTS && <div className="p-3 rounded border bg-amber-700/20 border-amber-700/50 text-amber-500">🚐 Vehicle</div>}
          <div className="p-3 rounded border bg-yellow-500/10 border-yellow-500/30 text-yellow-500">⚠️ Need Help</div>
          <div className="p-3 rounded border bg-orange-500/10 border-orange-500/30 text-orange-500">🚧 Blocked Route</div>
          <div className="p-3 rounded border bg-green-500/10 border-green-500/30 text-green-500">🧭 Meet Here</div>
          {ENFORCEMENT_REPORTS && <div className="p-3 rounded border bg-red-500/10 border-red-500/30 text-red-500">🛑 Danger Zone</div>}
          <div className="p-3 rounded border bg-pink-500/10 border-pink-500/30 text-pink-500">🏥 First Aid</div>
          <div className="p-3 rounded border bg-blue-500/10 border-blue-500/30 text-blue-500">✅ All Clear</div>
        </div>
        {ENFORCEMENT_REPORTS && (
          <p className="mt-4 text-xs text-white/50">
            <strong className="text-amber-500">🚐 Vehicle</strong> opens a detailed form where you can attach photos, enter plate info, and more.
          </p>
        )}
      </InfoCard>
    </div>
  )
}

function RolesSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <InfoCard title="Understanding Roles" variant="info">
        <p className="mb-3">
          CrowdTalkie uses a <strong className="text-primary">role-based access system</strong> to help
          organize groups effectively. Roles determine what actions you can take in a room.
        </p>
        <p className="text-xs text-white/60">
          There are two types of roles: <strong>Room Roles</strong> (for standard rooms) and
          <strong> Hierarchy Roles</strong> (for command structures).
        </p>
      </InfoCard>

      {/* Standard Room Roles */}
      <InfoCard title="Room Roles (Standard Rooms)">
        <p className="mb-4 text-sm text-white/70">
          Every room has three role levels. These control who can speak, send messages, and manage the room.
        </p>

        <div className="space-y-4">
          {/* Organizer/Admin */}
          <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">👑</span>
              <div>
                <div className="font-bold text-primary text-sm uppercase tracking-wider">Organizer (Admin)</div>
                <div className="text-[10px] text-white/50">Room creator • Full control</div>
              </div>
            </div>
            <div className="space-y-2 text-xs text-white/70">
              <p className="font-bold text-white/90 mb-2">Abilities:</p>
              <ul className="space-y-1 ml-4">
                <li>• Create and configure rooms</li>
                <li>• Set who can speak (everyone, mods, admin only)</li>
                <li>• Set chat mode (open, slow, mods only)</li>
                <li>• Set POI reporting permissions</li>
                <li>• Lock/unlock room access</li>
                <li>• Share room links and QR codes</li>
                <li>• Promote/demote participants to Coordinator</li>
                <li>• Remove participants from room</li>
                <li>• Pause or end the room</li>
                <li>• Recenter room location</li>
                <li>• Enable Command Mode (ICS)</li>
              </ul>
            </div>
          </div>

          {/* Coordinator/Mod */}
          <div className="p-4 rounded-xl bg-teal/10 border border-teal/30">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">⭐</span>
              <div>
                <div className="font-bold text-teal text-sm uppercase tracking-wider">Coordinator (Mod)</div>
                <div className="text-[10px] text-white/50">Trusted helper • Assist with management</div>
              </div>
            </div>
            <div className="space-y-2 text-xs text-white/70">
              <p className="font-bold text-white/90 mb-2">Abilities:</p>
              <ul className="space-y-1 ml-4">
                <li>• Speak even in "Mods Only" mic mode</li>
                <li>• Send messages in "Mods Only" chat mode</li>
                <li>• Report POIs in "Mods Only" POI mode</li>
                <li>• Clear POI markers from the map</li>
                <li>• View room controls panel (read-only)</li>
                <li>• Help moderate conversations</li>
              </ul>
              <p className="mt-3 text-[10px] text-white/50 italic">
                Note: Coordinators cannot change room settings, remove users, or end the room.
              </p>
            </div>
          </div>

          {/* Participant/Member */}
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">👤</span>
              <div>
                <div className="font-bold text-white/80 text-sm uppercase tracking-wider">Participant (Member)</div>
                <div className="text-[10px] text-white/50">Standard user • Basic access</div>
              </div>
            </div>
            <div className="space-y-2 text-xs text-white/70">
              <p className="font-bold text-white/90 mb-2">Abilities:</p>
              <ul className="space-y-1 ml-4">
                <li>• Join rooms within the geofence</li>
                <li>• Listen to voice messages</li>
                <li>• Speak when mic mode is "Everyone"</li>
                <li>• Send messages when chat mode is "Open"</li>
                <li>• Report POIs when POI mode is "Everyone"</li>
                <li>• View the map and POI markers</li>
                <li>• Leave the room at any time</li>
              </ul>
            </div>
          </div>
        </div>
      </InfoCard>

      {/* Hierarchy Roles */}
      <InfoCard title="Hierarchy Roles (Command Mode)">
        <p className="mb-4 text-sm text-white/70">
          When <strong className="text-primary">Command Mode</strong> is enabled, additional hierarchy roles
          become available for coordinating multiple sector rooms.
        </p>

        <div className="space-y-4">
          {/* Commander */}
          <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">👑</span>
              <div>
                <div className="font-bold text-primary text-sm uppercase tracking-wider">Commander</div>
                <div className="text-[10px] text-white/50">Command Room Admin • Top of hierarchy</div>
              </div>
            </div>
            <div className="space-y-2 text-xs text-white/70">
              <p className="font-bold text-white/90 mb-2">Abilities:</p>
              <ul className="space-y-1 ml-4">
                <li>• All Organizer abilities in the command room</li>
                <li>• Create and manage sector rooms</li>
                <li>• View all sectors on the Command Dashboard</li>
                <li>• Broadcast messages to all or specific sectors</li>
                <li>• Acknowledge escalated reports from sectors</li>
                <li>• Assign organizers to sector rooms</li>
                <li>• Update sector boundaries</li>
                <li>• Send remote commands to sectors (pause, resume)</li>
                <li>• Share command room with Deputies</li>
                <li>• View aggregated POIs from all sectors</li>
              </ul>
            </div>
          </div>

          {/* Deputy */}
          <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">🎖️</span>
              <div>
                <div className="font-bold text-purple-400 text-sm uppercase tracking-wider">Deputy Commander</div>
                <div className="text-[10px] text-white/50">Command Room Assistant • Shares oversight</div>
              </div>
            </div>
            <div className="space-y-2 text-xs text-white/70">
              <p className="font-bold text-white/90 mb-2">Abilities:</p>
              <ul className="space-y-1 ml-4">
                <li>• View the Command Dashboard</li>
                <li>• See all sector statuses and participant counts</li>
                <li>• Monitor pending escalations</li>
                <li>• Broadcast messages to sectors</li>
                <li>• Acknowledge escalated reports</li>
                <li>• View aggregated POIs on the map</li>
              </ul>
              <p className="mt-3 text-[10px] text-white/50 italic">
                Note: Deputies cannot create sectors, assign organizers, or share the command room.
              </p>
            </div>
          </div>

          {/* Observer */}
          <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">👁️</span>
              <div>
                <div className="font-bold text-blue-400 text-sm uppercase tracking-wider">Observer</div>
                <div className="text-[10px] text-white/50">Command Room Viewer • Read-only access</div>
              </div>
            </div>
            <div className="space-y-2 text-xs text-white/70">
              <p className="font-bold text-white/90 mb-2">Abilities:</p>
              <ul className="space-y-1 ml-4">
                <li>• View the Command Dashboard</li>
                <li>• See sector statuses and participant counts</li>
                <li>• Monitor escalations (cannot acknowledge)</li>
                <li>• View broadcasts</li>
                <li>• Participate in command room chat</li>
              </ul>
              <p className="mt-3 text-[10px] text-white/50 italic">
                Note: Observers cannot send broadcasts, acknowledge escalations, or make any changes.
              </p>
            </div>
          </div>

          {/* Sector Lead */}
          <div className="p-4 rounded-xl bg-teal/10 border border-teal/30">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">🎯</span>
              <div>
                <div className="font-bold text-teal text-sm uppercase tracking-wider">Sector Lead</div>
                <div className="text-[10px] text-white/50">Sector Room Admin • Local coordination</div>
              </div>
            </div>
            <div className="space-y-2 text-xs text-white/70">
              <p className="font-bold text-white/90 mb-2">Abilities:</p>
              <ul className="space-y-1 ml-4">
                <li>• All Organizer abilities in their sector room</li>
                <li>• Escalate important messages to Command</li>
                <li>• Set escalation priority (routine/urgent/critical)</li>
                <li>• Receive and display command broadcasts</li>
                <li>• Manage local team coordination</li>
                <li>• Send sector heartbeat updates to Command</li>
              </ul>
            </div>
          </div>

          {/* Team Member */}
          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-3 mb-3">
              <span className="text-2xl">👤</span>
              <div>
                <div className="font-bold text-white/80 text-sm uppercase tracking-wider">Team Member</div>
                <div className="text-[10px] text-white/50">Sector participant • Standard user</div>
              </div>
            </div>
            <div className="space-y-2 text-xs text-white/70">
              <p className="font-bold text-white/90 mb-2">Abilities:</p>
              <ul className="space-y-1 ml-4">
                <li>• All Participant abilities in sector rooms</li>
                <li>• See command broadcasts</li>
                <li>• Request the Sector Lead to escalate issues</li>
                <li>• Contribute to sector coordination</li>
              </ul>
            </div>
          </div>
        </div>
      </InfoCard>

      {/* Role Comparison Table */}
      <InfoCard title="Quick Role Comparison">
        <div className="overflow-x-auto">
          <table className="w-full text-[10px] font-mono">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left p-2 text-white/60">Ability</th>
                <th className="p-2 text-primary">Admin</th>
                <th className="p-2 text-teal">Mod</th>
                <th className="p-2 text-white/60">Member</th>
              </tr>
            </thead>
            <tbody className="text-white/70">
              <tr className="border-b border-white/5">
                <td className="p-2">Speak (Open mode)</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-green-500">✓</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="p-2">Speak (Mods Only)</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-red-500">✗</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="p-2">Speak (Admin Only)</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-red-500">✗</td>
                <td className="p-2 text-center text-red-500">✗</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="p-2">Clear POI markers</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-red-500">✗</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="p-2">Change room settings</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-red-500">✗</td>
                <td className="p-2 text-center text-red-500">✗</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="p-2">Remove users</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-red-500">✗</td>
                <td className="p-2 text-center text-red-500">✗</td>
              </tr>
              <tr className="border-b border-white/5">
                <td className="p-2">Promote to Mod</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-red-500">✗</td>
                <td className="p-2 text-center text-red-500">✗</td>
              </tr>
              <tr>
                <td className="p-2">End room</td>
                <td className="p-2 text-center text-green-500">✓</td>
                <td className="p-2 text-center text-red-500">✗</td>
                <td className="p-2 text-center text-red-500">✗</td>
              </tr>
            </tbody>
          </table>
        </div>
      </InfoCard>

      {/* Sharing with Roles */}
      <InfoCard title="Sharing with Specific Roles" variant="info">
        <p className="mb-4 text-sm text-white/70">
          When sharing a command room, you can specify what role the person will have:
        </p>

        <div className="space-y-3">
          <div className="p-3 rounded bg-purple-500/10 border border-purple-500/30">
            <div className="font-bold text-purple-400 text-xs uppercase tracking-wider mb-1">Share as Deputy</div>
            <p className="text-[10px] text-white/60">
              Use <strong>👑 Share Command</strong> → Select <strong>Deputy</strong>. They can help monitor and manage the hierarchy.
            </p>
          </div>

          <div className="p-3 rounded bg-blue-500/10 border border-blue-500/30">
            <div className="font-bold text-blue-400 text-xs uppercase tracking-wider mb-1">Share as Observer</div>
            <p className="text-[10px] text-white/60">
              Use <strong>👑 Share Command</strong> → Select <strong>Observer</strong>. They can watch but not make changes.
            </p>
          </div>

          <div className="p-3 rounded bg-teal/10 border border-teal/30">
            <div className="font-bold text-teal text-xs uppercase tracking-wider mb-1">Share Sector Link</div>
            <p className="text-[10px] text-white/60">
              From Command Dashboard, tap a sector's <strong>🔗 Share</strong> button to get a link for sector participants.
            </p>
          </div>
        </div>
      </InfoCard>
    </div>
  )
}

function OfflineSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <InfoCard title="Installing the App" variant="info">
        <p className="mb-4">
          Install CrowdTalkie on your phone for offline use. No app store needed!
        </p>

        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-black/20 border border-white/5">
            <div className="font-bold text-primary mb-2 text-xs uppercase tracking-wider">Chrome / Edge</div>
            <ol className="list-decimal list-inside space-y-1 text-xs font-mono text-white/60">
              <li>Open menu (three dots)</li>
              <li>Select <strong>"Install App"</strong></li>
              <li>Confirm installation</li>
            </ol>
          </div>

          <div className="p-4 rounded-xl bg-black/20 border border-white/5">
            <div className="font-bold text-primary mb-2 text-xs uppercase tracking-wider">Safari (iOS)</div>
            <ol className="list-decimal list-inside space-y-1 text-xs font-mono text-white/60">
              <li>Tap Share button</li>
              <li>Select <strong>"Add to Home Screen"</strong></li>
              <li>Confirm</li>
            </ol>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="Connection Status">
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded bg-black/20 border border-white/5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.8)]" />
              <span className="text-xs font-bold text-green-500 tracking-wider">P2P (5)</span>
            </div>
            <span className="text-xs text-white/50 font-mono">Direct connection to 5 people</span>
          </div>

          <div className="flex items-center gap-3 p-3 rounded bg-black/20 border border-white/5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-yellow-500 shadow-[0_0_5px_rgba(234,179,8,0.8)]" />
              <span className="text-xs font-bold text-yellow-500 tracking-wider">RELAY (2)</span>
            </div>
            <span className="text-xs text-white/50 font-mono">Connected via relay server</span>
          </div>

          <div className="flex items-center gap-3 p-3 rounded bg-black/20 border border-white/5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_5px_rgba(59,130,246,0.8)]" />
              <span className="text-xs font-bold text-blue-500 tracking-wider">BT (3)</span>
            </div>
            <span className="text-xs text-white/50 font-mono">Bluetooth connection nearby</span>
          </div>

          <div className="flex items-center gap-3 p-3 rounded bg-black/20 border border-white/5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-purple-500 shadow-[0_0_5px_rgba(168,85,247,0.8)]" />
              <span className="text-xs font-bold text-purple-500 tracking-wider">LoRa (2)</span>
            </div>
            <span className="text-xs text-white/50 font-mono">Long-range radio mesh (2-15km)</span>
          </div>

          <div className="flex items-center gap-3 p-3 rounded bg-black/20 border border-white/5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 shadow-[0_0_5px_rgba(239,68,68,0.8)] animate-pulse" />
              <span className="text-xs font-bold text-red-500 tracking-wider">NONE (0)</span>
            </div>
            <span className="text-xs text-white/50 font-mono">No connection</span>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="LoRa Mesh Mode" variant="info">
        <p className="mb-4">
          Long-range radio communication (2-15km) that works without internet or cell towers.
        </p>

        <div className="p-4 rounded-xl bg-black/20 border border-white/5 mb-4">
          <div className="font-bold text-primary mb-2 text-xs uppercase tracking-wider">Requirements</div>
          <ul className="list-disc list-inside space-y-1 text-xs font-mono text-white/60">
            <li>LoRa radio device (Heltec V3, T-Beam, etc.)</li>
            <li>MeshCore or Meshtastic firmware</li>
            <li>The CrowdTalkie iPhone app, or Chrome / Edge / Opera on Android and computers</li>
            <li>Bluetooth enabled on phone</li>
          </ul>
        </div>

        <div className="p-4 rounded-xl bg-black/20 border border-white/5 mb-4">
          <div className="font-bold text-primary mb-2 text-xs uppercase tracking-wider">How to connect</div>
          <ol className="list-decimal list-inside space-y-1 text-xs font-mono text-white/60">
            <li>Power on your radio (MeshCore companion, Bluetooth build)</li>
            <li>In a room, tap 📻 Radio at the top</li>
            <li>Tap "Connect to LoRa Device" and pick your radio</li>
            <li>Check "Your radio reports" is on the network you want</li>
            <li>If not, tap "Tune radio to this network"</li>
          </ol>
        </div>

        <div className="p-4 rounded-xl bg-black/20 border border-white/5">
          <div className="font-bold text-primary mb-2 text-xs uppercase tracking-wider">What goes over the radio</div>
          <ul className="list-disc list-inside space-y-1 text-xs font-mono text-white/60">
            <li>Text, quick actions (with a ~100 m location), and voice-note transcripts</li>
            <li>{ENFORCEMENT_REPORTS ? 'Not voice audio, photos, or vehicle sightings' : 'Not voice audio or photos'}</li>
            <li>One message every 5 seconds, to share the channel with the whole mesh</li>
            <li>Each room is a private channel; its key leaves your radio when you leave the room</li>
            <li>The default network is the Sacramento MeshCore mesh (909.875 MHz). Pick yours under Network</li>
            <li>CrowdTalkie will not transmit if your radio is set to a band that is not legal in your region</li>
          </ul>
        </div>

        <p className="mt-4 text-xs text-white/50">
          See <strong className="text-primary">Mesh Hardware</strong> in the navigation for device recommendations and setup guides.
        </p>
      </InfoCard>

      <InfoCard title="Bluetooth Mode" variant="info">
        <p className="mb-4">
          Works without internet when you're close to others.
        </p>

        <div className="p-4 rounded-xl bg-black/20 border border-white/5 mb-4">
          <div className="font-bold text-primary mb-2 text-xs uppercase tracking-wider">How to enable</div>
          <ol className="list-decimal list-inside space-y-1 text-xs font-mono text-white/60">
            <li>Tap the connection status</li>
            <li>Select "Enable Bluetooth"</li>
            <li>Allow Bluetooth access</li>
            <li>Scan for nearby people</li>
            <li>Connect with others nearby</li>
          </ol>
        </div>
      </InfoCard>

      <InfoCard title="Tips" variant="warning">
        <ul className="space-y-2 text-xs font-mono text-white/70">
          <li>• <span className="text-yellow-500">Install first:</span> Add to home screen before you need it</li>
          <li>• <span className="text-yellow-500">Maps:</span> Load the map while you have internet</li>
          <li>• <span className="text-yellow-500">Bluetooth range:</span> Stay within 30 meters of others</li>
          <li>• <span className="text-yellow-500">LoRa range:</span> Works 2-15km depending on terrain and settings</li>
          <li>• <span className="text-yellow-500">Battery:</span> Keep your phone and LoRa device charged</li>
          <li>• <span className="text-yellow-500">Test first:</span> Try offline connections before you need them</li>
        </ul>
      </InfoCard>
    </div>
  )
}

function SafetySection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <InfoCard title="Privacy Features" variant="info">
        <ul className="space-y-2 text-xs font-mono text-white/70">
          <li>• <strong className="text-primary">No accounts:</strong> No sign-up required</li>
          <li>• <strong className="text-primary">No tracking:</strong> We don't log your activity</li>
          <li>• <strong className="text-primary">Encrypted:</strong> Only your group can read messages</li>
          <li>• <strong className="text-primary">Temporary identity:</strong> New ID for each room</li>
        </ul>
      </InfoCard>

      <InfoCard title="Phone Security Tips" variant="warning">
        <ul className="space-y-2 text-xs font-mono text-white/70">
          <li>• Use a strong passcode</li>
          <li>• Turn off lock screen previews</li>
          <li>• Consider airplane mode for extra privacy</li>
          <li>• Clear browser data when done</li>
        </ul>
      </InfoCard>

      <InfoCard title="Communication Tips" variant="info">
        <ul className="space-y-2 text-xs font-mono text-white/70">
          <li>• Keep messages brief and clear</li>
          <li>• Say your name at the start</li>
          <li>• Avoid sharing exact addresses</li>
          <li>• Speak slowly and clearly</li>
        </ul>
      </InfoCard>

      <InfoCard title="Location Privacy" variant="warning">
        <ul className="space-y-2 text-xs font-mono text-white/70">
          <li>• Your exact location stays on your phone</li>
          <li>• Shared locations are approximate (~100m)</li>
          <li>• Only checks if you're in the room's area</li>
        </ul>
      </InfoCard>

      <InfoCard title="Room Access Security" variant="info">
        <p className="mb-3 text-sm">Organizers control who can join their rooms:</p>
        <ul className="space-y-2 text-xs font-mono text-white/70">
          <li>• <strong className="text-primary">Room Secret:</strong> Required password to join the room</li>
          <li>• <strong className="text-primary">PIN:</strong> Optional extra layer of protection</li>
          <li>• <strong className="text-primary">Access Requests:</strong> Users can request access if they don't have the secret</li>
          <li>• <strong className="text-primary">Auto-Approve:</strong> Organizers can auto-approve requests from users in the geofence</li>
          <li>• <strong className="text-primary">Security Delegate:</strong> Organizers can assign another member to handle access requests</li>
        </ul>
      </InfoCard>

      <InfoCard title="Location Accuracy Options" variant="info">
        <div className="space-y-3 mt-4">
          <div className="p-3 rounded border bg-green-500/5 border-green-500/20">
            <div className="font-bold text-green-500 text-xs mb-1 uppercase tracking-wider">🎯 Precise (GPS)</div>
            <p className="text-[10px] text-white/50">Most accurate. Uses more battery.</p>
          </div>

          <div className="p-3 rounded border bg-blue-500/5 border-blue-500/20">
            <div className="font-bold text-blue-500 text-xs mb-1 uppercase tracking-wider">📍 Balanced</div>
            <p className="text-[10px] text-white/50">Good accuracy using WiFi/cell towers.</p>
          </div>

          <div className="p-3 rounded border bg-yellow-500/5 border-yellow-500/20">
            <div className="font-bold text-yellow-500 text-xs mb-1 uppercase tracking-wider">🌐 Quick (IP)</div>
            <p className="text-[10px] text-white/50">Fast but less accurate (~5km).</p>
          </div>

          <div className="p-3 rounded border bg-purple-500/5 border-purple-500/20">
            <div className="font-bold text-purple-500 text-xs mb-1 uppercase tracking-wider">✏️ Manual</div>
            <p className="text-[10px] text-white/50">Enter your own location. Most private.</p>
          </div>
        </div>
      </InfoCard>
    </div>
  )
}

function FeaturesSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <InfoCard title="Encryption">
        <p>
          All messages are encrypted. Only people with the room secret can read them.
        </p>
      </InfoCard>

      <InfoCard title="Direct Connections">
        <p>
          Connects you directly to others. If internet is down, uses Bluetooth to reach nearby people.
        </p>
      </InfoCard>

      <InfoCard title="Voice Transcription">
        <p>
          Voice messages are automatically converted to text so you can read them later.
        </p>
      </InfoCard>

      <InfoCard title="Location Limits">
        <p>
          Rooms only work within a set distance (50-500m). This keeps conversations relevant to your area.
        </p>
      </InfoCard>

      <InfoCard title="Room Discovery">
        <p>
          Discover nearby rooms on the map. Join with a secret from the organizer or request access directly.
        </p>
      </InfoCard>

      <InfoCard title="Access Control">
        <p>
          Organizers can protect rooms with secrets and PINs. They can also allow access requests and delegate security management to trusted members.
        </p>
      </InfoCard>

      {/* New Twin Cities Features */}
      {ENFORCEMENT_REPORTS && (<>
      <InfoCard title="Vehicle Sighting Reports" variant="info">
        <p className="mb-3">
          Report suspicious vehicles with detailed information to keep your community informed.
        </p>
        <div className="space-y-2 text-xs font-mono text-white/70">
          <p><strong className="text-amber-500">🚐 Quick Report:</strong> Tap the Vehicle button in Quick Actions</p>
          <p><strong className="text-primary">📸 Photo Capture:</strong> Attach up to 3 photos from camera or gallery</p>
          <p><strong className="text-yellow-500">🔤 License Plate OCR:</strong> Auto-reads plates from photos</p>
          <p><strong className="text-green-500">📍 Geo-Tagging:</strong> Extracts GPS from photo EXIF or uses device location</p>
        </div>

        <div className="mt-4 p-3 rounded bg-black/30 border border-white/10">
          <div className="font-bold text-white/80 text-xs mb-2 uppercase tracking-wider">Vehicle Report Includes:</div>
          <ul className="space-y-1 text-[10px] text-white/60">
            <li>• License plate (auto-detected or manual)</li>
            <li>• Vehicle type (SUV, Sedan, Van, Truck, Unmarked)</li>
            <li>• Vehicle color</li>
            <li>• Direction of travel (compass + stationary)</li>
            <li>• "I'm following" toggle for active tracking</li>
            <li>• Photo evidence with thumbnails</li>
          </ul>
        </div>
      </InfoCard>

      <InfoCard title="Photo OCR & Geo-Tagging">
        <p className="mb-3">
          Photos attached to reports are automatically analyzed:
        </p>

        <div className="space-y-3">
          <div className="p-3 rounded bg-yellow-500/10 border border-yellow-500/30">
            <div className="font-bold text-yellow-500 text-xs mb-1 uppercase tracking-wider">📷 License Plate OCR</div>
            <p className="text-[10px] text-white/60">
              When you attach a photo, our OCR system reads any visible license plate and auto-fills the plate field.
              Works best with clear, well-lit photos of the plate.
            </p>
          </div>

          <div className="p-3 rounded bg-green-500/10 border border-green-500/30">
            <div className="font-bold text-green-500 text-xs mb-1 uppercase tracking-wider">📍 EXIF GPS Extraction</div>
            <p className="text-[10px] text-white/60">
              If your photo has embedded GPS data (from camera settings), we extract it automatically.
              This gives exact location where the photo was taken, not just where you are now.
            </p>
          </div>

          <div className="p-3 rounded bg-blue-500/10 border border-blue-500/30">
            <div className="font-bold text-blue-500 text-xs mb-1 uppercase tracking-wider">🗜️ Photo Compression</div>
            <p className="text-[10px] text-white/60">
              Photos are compressed to 1200px width for fast sharing over the mesh network.
              Thumbnails are generated for quick preview in chat.
            </p>
          </div>
        </div>

        <div className="mt-4 flex gap-2 text-[10px]">
          <span className="px-2 py-1 rounded bg-green-500/20 text-green-400">📍 = Has GPS location</span>
          <span className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">🚗 = Plate detected</span>
        </div>
      </InfoCard>
      </>)}

      <InfoCard title="Multi-Language Support">
        <p className="mb-3">
          CrowdTalkie supports multiple languages to serve diverse communities:
        </p>

        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 rounded bg-white/5 border border-white/10 text-center">
            <div className="text-lg mb-1">🇺🇸</div>
            <div className="text-xs font-bold text-white/80">English</div>
          </div>
          <div className="p-3 rounded bg-white/5 border border-white/10 text-center">
            <div className="text-lg mb-1">🇪🇸</div>
            <div className="text-xs font-bold text-white/80">Español</div>
          </div>
        </div>

        <p className="mt-4 text-[10px] text-white/50">
          Language is auto-detected from your browser settings. You can change it in Settings.
        </p>
      </InfoCard>

      {ENFORCEMENT_REPORTS && (<>
      <InfoCard title="Alert Sounds" variant="warning">
        <p className="mb-3">
          Audio alerts help you stay aware even when not looking at your phone:
        </p>

        <div className="space-y-2 text-xs font-mono">
          <div className="flex items-center gap-2 p-2 rounded bg-red-500/10 border-l-4 border-red-500">
            <span>🔴</span>
            <span className="text-red-400">Confirmed hostile vehicle</span>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-yellow-500/10 border-l-4 border-yellow-500">
            <span>🟡</span>
            <span className="text-yellow-400">Unknown vehicle sighting</span>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-orange-500/10 border-l-4 border-orange-500">
            <span>🟠</span>
            <span className="text-orange-400">Zone boundary warning</span>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-purple-500/10 border-l-4 border-purple-500">
            <span>🟣</span>
            <span className="text-purple-400">Handoff request</span>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-primary/10 border-l-4 border-primary">
            <span>🩷</span>
            <span className="text-primary">Whistle (mass alert)</span>
          </div>
        </div>

        <p className="mt-4 text-[10px] text-white/50">
          Configure alert volume, vibration, and radius filtering in Settings → Alerts.
        </p>
      </InfoCard>

      <InfoCard title="Zone Handoffs">
        <p className="mb-3">
          When tracking a vehicle across zones, hand off to the next team:
        </p>

        <ol className="list-decimal list-inside space-y-2 text-xs font-mono text-white/70">
          <li className="pl-2">You'll see a warning when approaching zone boundary</li>
          <li className="pl-2">Tap <strong className="text-primary">Initiate Handoff</strong> on the vehicle</li>
          <li className="pl-2">Adjacent zone members see the handoff request</li>
          <li className="pl-2">Someone accepts and picks up the tail</li>
          <li className="pl-2">You get confirmation of successful handoff</li>
        </ol>

        <p className="mt-4 text-[10px] text-white/50">
          Handoff requests expire after 2 minutes if not accepted.
        </p>
      </InfoCard>
      </>)}
    </div>
  )
}

function CommandSection() {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Feature Re-Enabled Notice */}
      <div className="p-4 rounded-xl bg-green-500/10 border-2 border-green-500/50">
        <div className="flex items-start gap-3">
          <span className="text-2xl">✅</span>
          <div>
            <div className="font-bold text-green-500 text-sm uppercase tracking-wider mb-2">
              Feature Active - January 2026
            </div>
            <p className="text-xs text-white/70 mb-3">
              The Command Center / Hierarchical ICS feature has been <strong className="text-green-400">re-enabled</strong> in
              response to urgent community needs. Rapid response networks organizing across the Twin Cities need robust,
              secure communication tools that work when cellular networks fail or are intentionally disrupted.
            </p>
            <p className="text-xs text-white/70 mb-3">
              The hierarchy is <strong>opt-in and advisory</strong>. Participants always retain full autonomy to act on
              immediate threats. Groups uncomfortable with hierarchy can continue using CrowdTalkie as standalone rooms.
            </p>
            <div className="p-3 rounded bg-black/30 border border-white/10">
              <p className="text-xs text-white/60 mb-2">
                <strong className="text-white/80">Feedback still welcome!</strong> Share your experiences and suggestions
                for improving these features:
              </p>
              <a
                href="mailto:craig@craigmerry.com?subject=CrowdTalkie Command Center Feedback"
                className="inline-flex items-center gap-2 text-xs font-mono text-primary hover:text-primary/80 transition-colors"
              >
                📧 craig@craigmerry.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <InfoCard title="Incident Command System (ICS)" variant="info">
        <p className="mb-3">
          CrowdTalkie supports <strong className="text-primary">multi-room hierarchies</strong> based on the
          Incident Command System used by emergency responders. This allows large groups to coordinate
          across multiple sectors while maintaining centralized oversight.
        </p>
        <p className="text-xs text-white/60">
          Perfect for large events, protests, festivals, or any situation requiring coordinated teams across different areas.
        </p>
      </InfoCard>

      <InfoCard title="Hierarchy Structure">
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-primary/10 border border-primary/30">
            <div className="font-bold text-primary text-sm mb-2 uppercase tracking-wider">📡 Command Room</div>
            <p className="text-xs text-white/70 mb-2">The central coordination hub. Commanders see all sectors, receive escalated reports, and broadcast to everyone.</p>
            <div className="text-[10px] text-white/50">One per hierarchy • Full visibility • Broadcast capability</div>
          </div>

          <div className="flex justify-center">
            <span className="text-white/30 text-2xl">↓</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-teal/10 border border-teal/30">
              <div className="font-bold text-teal text-xs mb-1 uppercase tracking-wider">📍 Sector A</div>
              <p className="text-[10px] text-white/60">North Team</p>
            </div>
            <div className="p-3 rounded-xl bg-teal/10 border border-teal/30">
              <div className="font-bold text-teal text-xs mb-1 uppercase tracking-wider">📍 Sector B</div>
              <p className="text-[10px] text-white/60">South Team</p>
            </div>
            <div className="p-3 rounded-xl bg-teal/10 border border-teal/30">
              <div className="font-bold text-teal text-xs mb-1 uppercase tracking-wider">📍 Sector C</div>
              <p className="text-[10px] text-white/60">East Team</p>
            </div>
            <div className="p-3 rounded-xl bg-teal/10 border border-teal/30">
              <div className="font-bold text-teal text-xs mb-1 uppercase tracking-wider">📍 Sector D</div>
              <p className="text-[10px] text-white/60">West Team</p>
            </div>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="Roles in the Hierarchy">
        <p className="mb-3 text-xs text-white/60">
          See the <strong className="text-primary">Roles</strong> tab for detailed permissions and abilities.
        </p>

        <div className="space-y-3">
          <div className="flex items-start gap-3 p-3 rounded bg-primary/10 border border-primary/30">
            <span className="text-lg">👑</span>
            <div>
              <div className="font-bold text-primary text-xs uppercase tracking-wider">Commander</div>
              <p className="text-[10px] text-white/50 mt-1">Creates and manages the command room. Full control over hierarchy. Creates sectors, broadcasts to all, acknowledges escalations, assigns organizers.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 rounded bg-purple-500/10 border border-purple-500/30">
            <span className="text-lg">🎖️</span>
            <div>
              <div className="font-bold text-purple-400 text-xs uppercase tracking-wider">Deputy Commander</div>
              <p className="text-[10px] text-white/50 mt-1">Assists commander with monitoring and management. Can view dashboard, send broadcasts, and acknowledge escalations. Cannot create sectors or share command room.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 rounded bg-blue-500/10 border border-blue-500/30">
            <span className="text-lg">👁️</span>
            <div>
              <div className="font-bold text-blue-400 text-xs uppercase tracking-wider">Observer</div>
              <p className="text-[10px] text-white/50 mt-1">View-only access to the Command Dashboard. Can see sector statuses and escalations but cannot make changes or send broadcasts.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 rounded bg-teal/10 border border-teal/30">
            <span className="text-lg">🎯</span>
            <div>
              <div className="font-bold text-teal text-xs uppercase tracking-wider">Sector Lead</div>
              <p className="text-[10px] text-white/50 mt-1">Manages one sector room. Can escalate important messages to command. Coordinates local team. Receives broadcasts from command.</p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-3 rounded bg-white/5 border border-white/10">
            <span className="text-lg">👤</span>
            <div>
              <div className="font-bold text-white/70 text-xs uppercase tracking-wider">Team Member</div>
              <p className="text-[10px] text-white/50 mt-1">Participates in sector rooms. Sends reports and voice messages within their sector. Sees command broadcasts.</p>
            </div>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="Setting Up Command" variant="info">
        <p className="mb-3 text-sm font-bold">For Commanders:</p>
        <ol className="list-decimal list-inside space-y-3 font-mono text-sm text-white/70">
          <li className="pl-2">Create a new room as usual</li>
          <li className="pl-2">Open <strong className="text-primary">Room Controls</strong></li>
          <li className="pl-2">Select <strong className="text-primary">👑 Enable Command Mode</strong></li>
          <li className="pl-2">Your room becomes the Command Room</li>
          <li className="pl-2">The <strong className="text-primary">Command Dashboard</strong> appears above Room Controls</li>
          <li className="pl-2">Create sectors using <strong className="text-primary">➕ Add Sector</strong> in the dashboard</li>
          <li className="pl-2">Share sector links with sector leads</li>
          <li className="pl-2">Optionally add deputies using <strong className="text-primary">👑 Share Command</strong></li>
        </ol>
      </InfoCard>

      <InfoCard title="Inviting Deputies" variant="info">
        <p className="mb-3 text-sm">To add a Deputy Commander who can help manage the hierarchy:</p>
        <ol className="list-decimal list-inside space-y-3 font-mono text-sm text-white/70">
          <li className="pl-2">Open the <strong className="text-primary">Command Dashboard</strong></li>
          <li className="pl-2">Tap <strong className="text-primary">👑 Share Command</strong></li>
          <li className="pl-2">Select <strong className="text-purple-400">🎖️ Deputy</strong> role</li>
          <li className="pl-2">Share the link and secret with your deputy</li>
          <li className="pl-2">They'll join with broadcast and acknowledgment abilities</li>
        </ol>
        <p className="mt-4 text-xs text-white/50">
          Use <strong>👁️ Observer</strong> role for people who just need to monitor without making changes.
        </p>
      </InfoCard>

      <InfoCard title="Creating Sectors">
        <p className="mb-3 text-sm">From the Command Dashboard:</p>
        <ol className="list-decimal list-inside space-y-3 font-mono text-sm text-white/70">
          <li className="pl-2">Tap <strong className="text-primary">+ Add Sector</strong></li>
          <li className="pl-2">Name the sector (e.g., "North Street Team")</li>
          <li className="pl-2">Set the sector's center location</li>
          <li className="pl-2">Choose a geofence radius (50m - 500m)</li>
          <li className="pl-2">Assign a Sector Lead (optional)</li>
          <li className="pl-2">Tap <strong className="text-primary">Create Sector</strong></li>
        </ol>
        <p className="mt-4 text-xs text-white/50">
          Each sector operates as its own room with its own geofence. Sector leads manage their local team.
        </p>
      </InfoCard>

      <InfoCard title="Escalating Messages" variant="warning">
        <p className="mb-3 text-sm">Sector Leads can escalate important messages to command:</p>
        <ol className="list-decimal list-inside space-y-3 font-mono text-sm text-white/70">
          <li className="pl-2">Long-press on a message in your sector</li>
          <li className="pl-2">Select <strong className="text-primary">⬆️ Escalate to Command</strong></li>
          <li className="pl-2">Choose priority level:</li>
        </ol>

        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2 p-2 rounded bg-green-500/10 border-l-4 border-green-500">
            <span>🟢</span>
            <div>
              <span className="text-xs font-bold text-green-500">Routine</span>
              <span className="text-[10px] text-white/50 ml-2">For command awareness</span>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-orange-500/10 border-l-4 border-orange-500">
            <span>🟡</span>
            <div>
              <span className="text-xs font-bold text-orange-500">Urgent</span>
              <span className="text-[10px] text-white/50 ml-2">Needs attention soon</span>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 rounded bg-red-500/10 border-l-4 border-red-500">
            <span>🔴</span>
            <div>
              <span className="text-xs font-bold text-red-500">Critical</span>
              <span className="text-[10px] text-white/50 ml-2">Immediate attention required</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="Broadcasting Commands">
        <p className="mb-3 text-sm">Commanders can broadcast to all or specific sectors:</p>
        <ol className="list-decimal list-inside space-y-3 font-mono text-sm text-white/70">
          <li className="pl-2">From Command Dashboard, tap <strong className="text-primary">📢 Broadcast</strong></li>
          <li className="pl-2">Select target: <strong>All Sectors</strong> or specific sectors</li>
          <li className="pl-2">Choose priority level</li>
          <li className="pl-2">Type your message</li>
          <li className="pl-2">Tap <strong className="text-primary">Send Broadcast</strong></li>
        </ol>
        <p className="mt-4 text-xs text-white/50">
          Broadcasts appear as prominent banners in sector rooms, ensuring visibility.
        </p>
      </InfoCard>

      <InfoCard title="Command Dashboard">
        <p className="mb-3 text-sm">The Command Dashboard is organized into three tabs:</p>

        <div className="space-y-3 mb-4">
          <div className="p-3 rounded bg-primary/10 border border-primary/30">
            <div className="font-bold text-primary text-xs mb-1 uppercase tracking-wider">📊 Overview Tab</div>
            <ul className="text-[10px] text-white/60 space-y-1">
              <li>• Quick stats: sector count, total personnel, pending alerts</li>
              <li>• Sector status summary with color-coded indicators</li>
              <li>• Map visualization of all sectors and POIs</li>
              <li>• Quick actions: Add Sector, Share Command, Broadcast</li>
            </ul>
          </div>

          <div className="p-3 rounded bg-teal/10 border border-teal/30">
            <div className="font-bold text-teal text-xs mb-1 uppercase tracking-wider">📍 Sectors Tab</div>
            <ul className="text-[10px] text-white/60 space-y-1">
              <li>• Detailed sector management cards</li>
              <li>• Participant lists per sector</li>
              <li>• Share, edit boundary, and delete sector actions</li>
              <li>• Send remote commands to individual sectors</li>
            </ul>
          </div>

          <div className="p-3 rounded bg-red-500/10 border border-red-500/30">
            <div className="font-bold text-red-400 text-xs mb-1 uppercase tracking-wider">⚠️ Alerts Tab</div>
            <ul className="text-[10px] text-white/60 space-y-1">
              <li>• Pending escalations from all sectors</li>
              <li>• Priority-sorted (critical → urgent → routine)</li>
              <li>• One-tap acknowledgment</li>
              <li>• "All Clear" indicator when no pending alerts</li>
            </ul>
          </div>
        </div>

        <p className="text-xs font-bold text-white/80 mb-2">Sector Status Indicators:</p>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-[10px]">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <span className="text-white/50">Active - Normal operation</span>
          </div>
          <div className="flex items-center gap-2 text-[10px]">
            <span className="w-2 h-2 rounded-full bg-orange-500"></span>
            <span className="text-white/50">Alert - Situation developing</span>
          </div>
          <div className="flex items-center gap-2 text-[10px]">
            <span className="w-2 h-2 rounded-full bg-gray-500"></span>
            <span className="text-white/50">Offline - No recent heartbeat</span>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="Best Practices" variant="info">
        <div className="space-y-4">
          <div>
            <div className="font-bold text-primary text-xs mb-2 uppercase tracking-wider">Planning</div>
            <ul className="space-y-1 text-xs font-mono text-white/70">
              <li>• Establish command structure before the event</li>
              <li>• Pre-assign sector leads and brief them on escalation criteria</li>
              <li>• Test the hierarchy with a dry run</li>
              <li>• Share sector links securely before deployment</li>
            </ul>
          </div>

          <div>
            <div className="font-bold text-teal text-xs mb-2 uppercase tracking-wider">During Operations</div>
            <ul className="space-y-1 text-xs font-mono text-white/70">
              <li>• Sector leads: Only escalate what command needs to know</li>
              <li>• Commanders: Acknowledge escalations promptly</li>
              <li>• Use appropriate priority levels - don't over-escalate</li>
              <li>• Keep broadcasts concise and actionable</li>
            </ul>
          </div>

          <div>
            <div className="font-bold text-yellow-500 text-xs mb-2 uppercase tracking-wider">Communication</div>
            <ul className="space-y-1 text-xs font-mono text-white/70">
              <li>• Use clear, standardized language</li>
              <li>• Identify yourself when escalating</li>
              <li>• Include location in reports when relevant</li>
              <li>• Confirm receipt of critical broadcasts</li>
            </ul>
          </div>
        </div>
      </InfoCard>

      <InfoCard title="Cross-Room Identity" variant="info">
        <p className="mb-3 text-sm">
          Your identity is cryptographically linked across the hierarchy while remaining private:
        </p>
        <ul className="space-y-2 text-xs font-mono text-white/70">
          <li>• <strong className="text-primary">Single Login:</strong> One identity works across command and all sectors</li>
          <li>• <strong className="text-primary">Role Verification:</strong> Your role is cryptographically verified</li>
          <li>• <strong className="text-primary">Privacy Preserved:</strong> Different derived keys per room prevent tracking</li>
          <li>• <strong className="text-primary">Signed Messages:</strong> All escalations and broadcasts are signed</li>
        </ul>
      </InfoCard>
    </div>
  )
}

function FAQSection() {
  const faqs = [
    {
      q: "Do I need to create an account?",
      a: "No. CrowdTalkie works without accounts or sign-ups."
    },
    {
      q: "Can others intercept my messages?",
      a: "Messages are encrypted. Only people with the room link and secret can read them."
    },
    {
      q: "What if I lose internet?",
      a: "CrowdTalkie tries direct connections first. You can also use Bluetooth for nearby people."
    },
    {
      q: "Can I use it without GPS?",
      a: "Yes! You can enter your location manually."
    },
    {
      q: "Is my data stored?",
      a: "Data stays on your phone only and is deleted when you leave the room."
    },
    {
      q: "How do I join a room I found on the map?",
      a: "Tap the room marker and choose to enter the secret (if you have it) or request access from the organizer."
    },
    {
      q: "What is a room secret vs PIN?",
      a: "The room secret is the main password to join. The PIN is an optional extra layer of protection some organizers use."
    },
    {
      q: "What's the difference between Admin, Mod, and Member?",
      a: "Admin (Organizer) has full control - settings, removing users, ending the room. Mod (Coordinator) can speak/chat in restricted modes and clear POIs. Member (Participant) has basic access based on room settings."
    },
    {
      q: "How do I make someone a Moderator?",
      a: "As the room organizer, open Room Controls, find the person in the participant list, tap the menu [...] and select 'Make Moderator'."
    },
    {
      q: "What is Command Mode?",
      a: "Command Mode turns your room into a command center that can oversee multiple sector rooms. It's based on the Incident Command System used by emergency responders."
    },
    {
      q: "What's the difference between Commander, Deputy, and Observer?",
      a: "Commander has full control. Deputy can monitor and send broadcasts but can't create sectors or share the command room. Observer can only view the dashboard without making changes."
    },
    {
      q: "How do I add a Deputy to help me manage?",
      a: "In the Command Dashboard, tap '👑 Share Command', select 'Deputy' role, and share the link with your deputy. They'll be able to help with broadcasts and escalations."
    },
    {
      q: "How do I escalate a message to command?",
      a: "Long-press on any message in your sector room and select 'Escalate to Command'. Choose the priority level (routine, urgent, or critical) and confirm."
    },
    {
      q: "Can I be in multiple sectors at once?",
      a: "You can only be active in one room at a time, but your identity works across the hierarchy. Switch rooms as needed."
    },
    {
      q: "What happens if a sector goes offline?",
      a: "Command will see the sector status change to 'Offline' after missing several heartbeats. Sector leads should ensure someone is always active."
    },
    {
      q: "Where do I add new sectors?",
      a: "Sectors are added from the Command Dashboard (not Room Controls). Tap '➕ Add Sector' to create a new sector room."
    },
    {
      q: "How do I report a vehicle sighting?",
      a: "Tap the 🚐 Vehicle button in Quick Actions. You can attach photos, and the app will automatically try to read the license plate from your photo."
    },
    {
      q: "Does the app read license plates automatically?",
      a: "Yes! When you attach a photo to a vehicle report, our OCR system attempts to detect and read any visible license plate. It works best with clear, well-lit photos."
    },
    {
      q: "What is geo-tagging for photos?",
      a: "If your photo has GPS data embedded (from your camera settings), we extract the exact location where the photo was taken. This is more accurate than your current location."
    },
    {
      q: "How do zone handoffs work?",
      a: "When you're tracking a vehicle and approach a zone boundary, you can initiate a handoff. Members in the adjacent zone will see the request and can accept to continue tracking."
    },
    {
      q: "Can I change the app language?",
      a: "Yes! Go to Settings → Language. Currently English and Spanish are supported. The app also auto-detects your browser language."
    },
    {
      q: "What is LoRa mesh and how does it work?",
      a: "LoRa is long-range radio (2-15km range) that works without internet or cell towers. Connect a MeshCore or Meshtastic device via Bluetooth to extend your range when cellular networks fail."
    },
    {
      q: "Which browsers support LoRa connections?",
      a: "On iPhone, use the CrowdTalkie app: Safari cannot reach Bluetooth radios. On Android and computers, Chrome, Edge and Opera connect over Web Bluetooth; Firefox cannot."
    },
    {
      q: "Do I need special hardware for LoRa?",
      a: "Yes, you need a LoRa radio device like a Heltec V3, LILYGO T-Beam, or RAK WisBlock running MeshCore or Meshtastic firmware. See the Mesh Hardware page for recommendations."
    },
  ]

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      {faqs.filter((faq) => ENFORCEMENT_REPORTS || !ENFORCEMENT_FAQ.test(faq.q)).map((faq, i) => (
        <InfoCard key={i} title={faq.q}>
          <p className="text-white/70 font-mono text-xs">{faq.a}</p>
        </InfoCard>
      ))}
    </div>
  )
}

